import { useState, useEffect } from 'react';
import { Box } from '@chakra-ui/react';

import { MarketCredentialsSection } from '../components';
import { useAnswerCodes } from '../hooks';
import {
  PropertyPolicyBasicInfo,
  InsuranceHistoryCard,
  type PropertyPolicyInfoData,
  type PropertyInsuranceHistoryData,
} from './policy-info';

interface PropertyPolicyInfoStepProps {
  data: PropertyPolicyInfoData;
  onUpdate: (
    data: Partial<PropertyPolicyInfoData['policy_info']> & {
      effective_date?: string;
    },
  ) => void;
  isUpdating?: boolean;
}

const PropertyPolicyInfoStep = ({
  data,
  onUpdate,
  isUpdating,
}: PropertyPolicyInfoStepProps) => {
  const { isEffectiveDateValid } = useAnswerCodes();

  const policyInfo = data.policy_info || {};

  const [effectiveDate, setEffectiveDate] = useState(data.effective_date || '');
  const [customerType, setCustomerType] = useState(
    policyInfo.customer_type || 'individual',
  );
  const [insuranceHistory, setInsuranceHistory] =
    useState<PropertyInsuranceHistoryData>(
      policyInfo.insurance_history || {
        apply_for_all_carriers: true,
        cancelled_for_non_payment: false,
        cancelled_for_fraud: false,
      },
    );

  useEffect(() => {
    setEffectiveDate(data.effective_date || '');
    setCustomerType(policyInfo.customer_type || 'individual');
    setInsuranceHistory(
      policyInfo.insurance_history || {
        apply_for_all_carriers: true,
        cancelled_for_non_payment: false,
        cancelled_for_fraud: false,
      },
    );
  }, [data, policyInfo]);

  const handleFieldChange = (field: string, value: any) => {
    const updateData: any = {};

    switch (field) {
      case 'effective_date':
        setEffectiveDate(value);
        updateData.effective_date = value;
        break;
      case 'customer_type':
        setCustomerType(value);
        updateData.customer_type = value;
        break;
    }

    onUpdate(updateData);
  };

  const handleInsuranceHistoryChange = (
    updates: Partial<PropertyInsuranceHistoryData>,
  ) => {
    const updatedHistory = { ...insuranceHistory, ...updates };
    setInsuranceHistory(updatedHistory);
    onUpdate({ insurance_history: updatedHistory });
  };

  return (
    <Box>
      <PropertyPolicyBasicInfo
        effectiveDate={effectiveDate}
        customerType={customerType}
        onFieldChange={handleFieldChange}
        isUpdating={isUpdating}
      />

      <InsuranceHistoryCard
        insuranceHistory={insuranceHistory}
        onInsuranceHistoryChange={handleInsuranceHistoryChange}
        onFieldChange={handleFieldChange}
        isUpdating={isUpdating}
      />

      <Box mt={4}>
        <MarketCredentialsSection
          quoteId={data.id}
          isEffectiveDateValid={isEffectiveDateValid}
        />
      </Box>
    </Box>
  );
};

export default PropertyPolicyInfoStep;
